import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { MessageSquare, Plus, Send, CheckCircle2, Clock, MailQuestion, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  open: { label: 'Ouvert', cls: 'bg-amber-100 text-amber-800', icon: Clock },
  replied: { label: 'Réponse', cls: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  closed: { label: 'Fermé', cls: 'bg-gray-100 text-gray-700', icon: CheckCircle2 },
};

const Support = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({ subject: '', body: '' });
  const [sending, setSending] = useState(false);
  const [activeTicket, setActiveTicket] = useState(null);
  const [replyBody, setReplyBody] = useState('');
  const [replying, setReplying] = useState(false);
  const scrollRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => { loadTickets(); }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [activeTicket]);

  const loadTickets = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/support/messages?user_id=${user.id}`);
      const data = await res.json();
      setTickets(data || []);
    } catch (e) { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newForm.subject.trim() || !newForm.body.trim()) { toast.error('Sujet et message requis'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API_BASE}/api/support/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, subject: newForm.subject, body: newForm.body }),
      });
      if (!res.ok) throw new Error();
      toast.success('Message envoyé. Nous vous répondrons rapidement.');
      setNewForm({ subject: '', body: '' });
      setNewOpen(false);
      await loadTickets();
    } catch (e) { toast.error('Erreur'); }
    finally { setSending(false); }
  };

  const openTicket = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/api/support/messages/${id}?user_id=${user.id}`);
      const data = await res.json();
      setActiveTicket(data);
    } catch (e) { toast.error('Erreur'); }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyBody.trim()) return;
    setReplying(true);
    try {
      const res = await fetch(`${API_BASE}/api/support/messages/${activeTicket.id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, body: replyBody }),
      });
      const data = await res.json();
      setActiveTicket(data);
      setReplyBody('');
      loadTickets();
    } catch (e) { toast.error('Erreur'); }
    finally { setReplying(false); }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <h1 className="text-4xl font-semibold mb-2 tracking-tight flex items-center gap-3">
            <MessageSquare size={32} className="text-[#205F79]" />
            Support
          </h1>
          <p className="text-muted-foreground">Une question, un bug, une suggestion ? Contactez l'équipe.</p>
        </div>
        <Button onClick={() => setNewOpen(true)} className="gap-2 bg-[#205F79] hover:bg-[#184a5e]" data-testid="btn-new-ticket">
          <Plus size={18} /> Nouveau message
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Mes messages</CardTitle>
          <CardDescription>{tickets.length} conversation{tickets.length > 1 ? 's' : ''}</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="skeleton-shimmer h-32 rounded-lg" />
          ) : tickets.length === 0 ? (
            <div className="text-center py-12">
              <MailQuestion className="mx-auto text-muted-foreground mb-3" size={48} strokeWidth={1.3} />
              <p className="text-sm text-muted-foreground">Aucun message — envoyez votre première demande.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {tickets.map(t => {
                const st = STATUS_LABEL[t.status] || STATUS_LABEL.open;
                const StIcon = st.icon;
                const last = t.replies?.[t.replies.length - 1];
                return (
                  <button
                    key={t.id}
                    onClick={() => openTicket(t.id)}
                    data-testid={`ticket-row-${t.id}`}
                    className="w-full text-left flex items-center gap-3 p-4 border border-border rounded-md hover:border-[#205F79]/50 hover:bg-muted/40 transition-all"
                  >
                    <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${st.cls}`}>
                      <StIcon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="font-medium truncate">{t.subject}</p>
                        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${st.cls}`}>
                          {st.label}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {last?.from === 'admin' && '[Admin] '}{last?.body}
                      </p>
                    </div>
                    <ChevronRight size={16} className="text-muted-foreground shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* New ticket dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouveau message</DialogTitle>
            <DialogDescription>Décrivez votre problème ou votre question.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Sujet</label>
              <Input value={newForm.subject} onChange={(e) => setNewForm({ ...newForm, subject: e.target.value })} required data-testid="input-ticket-subject" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Message</label>
              <Textarea rows={6} value={newForm.body} onChange={(e) => setNewForm({ ...newForm, body: e.target.value })} required data-testid="input-ticket-body" />
            </div>
            <Button type="submit" disabled={sending} className="w-full gap-2 bg-[#205F79] hover:bg-[#184a5e]" data-testid="btn-send-ticket">
              {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
              {sending ? 'Envoi...' : 'Envoyer'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Conversation dialog */}
      <Dialog open={!!activeTicket} onOpenChange={(open) => !open && setActiveTicket(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{activeTicket?.subject}</DialogTitle>
            <DialogDescription>
              {activeTicket && STATUS_LABEL[activeTicket.status]?.label}
            </DialogDescription>
          </DialogHeader>
          <div ref={scrollRef} className="max-h-[60vh] overflow-y-auto space-y-3 py-2">
            {activeTicket?.replies?.map((r, i) => (
              <div key={i} className={`flex ${r.from === 'admin' ? 'justify-start' : 'justify-end'}`}>
                <div className={`max-w-[80%] rounded-lg px-4 py-2.5 ${r.from === 'admin' ? 'bg-[#205F79]/10 border border-[#205F79]/20' : 'bg-amber-50 border border-amber-200'}`}>
                  <p className="text-[10px] uppercase tracking-wider font-semibold mb-1 opacity-70">
                    {r.from === 'admin' ? `Support · ${r.author_name}` : 'Vous'}
                  </p>
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{r.body}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">{new Date(r.created_at).toLocaleString('fr-FR')}</p>
                </div>
              </div>
            ))}
          </div>
          {activeTicket?.status !== 'closed' && (
            <form onSubmit={handleReply} className="flex gap-2 pt-2 border-t">
              <Input
                placeholder="Votre réponse..."
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                data-testid="input-reply-body"
              />
              <Button type="submit" disabled={replying || !replyBody.trim()} className="gap-2 bg-[#205F79] hover:bg-[#184a5e]" data-testid="btn-send-reply">
                {replying ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
              </Button>
            </form>
          )}
          {activeTicket?.status === 'closed' && (
            <p className="text-xs text-center text-muted-foreground pt-2 border-t">Cette conversation a été clôturée.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Support;
